/**
 * @file `TerminalOverlay` — translucent dim layer + per-state copy
 *       rendered above the terminal canvas during non-`running`
 *       lifecycle states. Driven by the `Terminal` client's
 *       `lifecycleState`; no overlay renders during `running` (the
 *       terminal is interactive).
 * @module @labcraft/lab/ui/components/terminal-overlay
 *
 * Per umbrella #824 the wire vocabulary expanded to eleven states.
 * The SPA's render vocabulary stays coarser — every state collapses
 * onto one of six treatments:
 *
 *   provisioning, provisioned, starting, started → spinner + "Booting your lab…"
 *   running                                      → no overlay
 *   pausing, paused, snapshotting                → dim + "Pausing your lab…"
 *   saved                                        → dim + "Paused, snapshotted · resumes in ~2s"
 *   resuming                                     → dim + spinner + "Resuming…"
 *   terminating                                  → dim + "Your previous lab expired. Start a fresh one?"
 *
 * The `terminating` action is left to the page-level layer (the chapter
 * page knows how to rebuild engagement); `TerminalOverlay` exposes an
 * optional `onExpiredRestart` callback the caller can wire.
 */

import type { JSX } from 'react'
import type {
  BudgetExhaustedPayload,
  MachineLifecycleState,
} from '../../shared/ws_messages.ts'
import { OutOfTimeTakeover } from './out_of_time_takeover.tsx'
import styles from './terminal_overlay.module.css'

/**
 * Props for {@link TerminalOverlay}.
 *
 * @property state - Current VM lifecycle state. `null` during the
 *   sub-second window between WS attach and the first `lifecycle`
 *   frame; renders as `booting` (spinner) until the first frame
 *   arrives.
 * @property onExpiredRestart - Optional handler bound to the
 *   "Start a fresh one" button rendered in the `expired` state.
 * @property budgetExhausted - Set once a `budget-exhausted` frame
 *   arrives (#834). Takes precedence over `state`: the VM was
 *   snapshotted, so the dim + upgrade CTA replaces whatever transient
 *   lifecycle overlay would otherwise show.
 * @property contentRefreshed - Set for the duration of the clean-refresh
 *   safety-net fresh-boot (umbrella #1258 Part D / #1333): a saved
 *   session whose pinned overlay content was force-expired is being
 *   cold-booted on the current version. Takes precedence over the
 *   transient `terminating` / boot copy so the learner sees an EXPLICIT
 *   "moved you to the current content" message rather than the
 *   misleading "your previous lab expired" or a silent scrollback loss.
 */
export type TerminalOverlayProps = {
  state: MachineLifecycleState | null
  onExpiredRestart?: () => void
  budgetExhausted?: BudgetExhaustedPayload | null
  contentRefreshed?: boolean
}

/**
 * Render gate. We only paint an overlay when the backend explicitly
 * told us the VM is in a non-running state. `null` means "no
 * lifecycle frame yet" — render nothing and let the terminal show.
 *
 * This is what keeps an old hypervisor (no lifecycle emission) from
 * trapping a perfectly-alive shell behind a permanent "Booting"
 * spinner: bytes flow through the WS, the prompt paints, and the
 * absence of state cleanly resolves to "no overlay" rather than
 * defaulting to `booting`. A new hypervisor that emits the explicit
 * `booting` → `running` transition still gets the spinner because
 * the explicit state lands before the prompt does.
 */
export function TerminalOverlay(
  props: TerminalOverlayProps,
): JSX.Element | null {
  // #834/#941: the daily free-tier cap is a deliberate conversion
  // moment that takes precedence over the lifecycle overlay. The VM was
  // snapshotted on the way down, so a resume after upgrade (or tomorrow)
  // restores exactly where the learner hit the wall. #941 renders the
  // calm full-pane takeover (local reset time + "go unlimited") rather
  // than a status dim.
  if (props.budgetExhausted) {
    return <OutOfTimeTakeover />
  }
  if (props.state === null) return null
  if (props.state === 'running') return null

  // #1333: the clean-refresh safety net is cold-booting a fresh VM
  // because this saved session's pinned overlay content was force-
  // expired (a security base bump / pruned layer). Surface it
  // explicitly for the whole terminating → boot sequence — never the
  // misleading "your previous lab expired" copy, never a silent reset.
  if (props.contentRefreshed) {
    return (
      <div
        className={`${styles.overlay} ${styles.dimOverlay} ${styles.spinnerOverlay}`}
        role="status"
        aria-live="polite">
        <div className={styles.spinner} aria-hidden />
        <p className={styles.label}>
          Updating your lab to the current content…
        </p>
        <p className={styles.subLabel}>
          Your saved session was on an older version. We’ve moved you to the
          current content — your chapter progress is saved.
        </p>
      </div>
    )
  }

  switch (props.state) {
    case 'provisioning':
    case 'provisioned':
    case 'starting':
    case 'started':
      return (
        <div
          className={`${styles.overlay} ${styles.spinnerOverlay}`}
          role="status"
          aria-live="polite">
          <div className={styles.spinner} aria-hidden />
          <p className={styles.label}>Booting your lab…</p>
        </div>
      )
    case 'pausing':
    case 'paused':
    case 'snapshotting':
      return (
        <div
          className={`${styles.overlay} ${styles.dimOverlay}`}
          role="status"
          aria-live="polite">
          <p className={styles.label}>Pausing your lab…</p>
        </div>
      )
    case 'saved':
      // Umbrella #926: the VM was snapshotted live on suspend, so a
      // resume restores the learner to exactly where they left off
      // (terminal scrollback, editor buffer, running processes). The
      // hero reflects that — "paused, snapshotted · resumes in ~2s".
      return (
        <div
          className={`${styles.overlay} ${styles.dimOverlay}`}
          role="status"
          aria-live="polite">
          <p className={styles.label}>Paused, snapshotted · resumes in ~2s</p>
          <p className={styles.subLabel}>
            Type to pick up exactly where you left off.
          </p>
        </div>
      )
    case 'resuming':
      return (
        <div
          className={`${styles.overlay} ${styles.dimOverlay} ${styles.spinnerOverlay}`}
          role="status"
          aria-live="polite">
          <div className={styles.spinner} aria-hidden />
          <p className={styles.label}>Resuming…</p>
        </div>
      )
    case 'terminating':
      return (
        <div
          className={`${styles.overlay} ${styles.dimOverlay}`}
          role="status"
          aria-live="polite">
          <p className={styles.label}>Your previous lab expired.</p>
          {props.onExpiredRestart && (
            <button
              className={styles.restartBtn}
              type="button"
              onClick={props.onExpiredRestart}>
              Start a fresh one
            </button>
          )}
        </div>
      )
    case 'failed':
      // #1009: a hung snapshot left the shell un-recoverable. This dim
      // is only the transient in-stream state; the actionable recovery
      // (the "start a fresh shell" pane takeover) is driven by the 4005
      // WS-close in chapter.tsx, so no button here — it isn't resumable.
      return (
        <div
          className={`${styles.overlay} ${styles.dimOverlay}`}
          role="status"
          aria-live="polite">
          <p className={styles.label}>This shell hit a snag…</p>
        </div>
      )
  }
}
